import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import ReactDOM from "react-dom";
import {
    useReactTable,
    getCoreRowModel,
    flexRender,
    functionalUpdate,
} from "@tanstack/react-table";
import {
    MoreHorizontal,
    ChevronDown,
    GripVertical,
    GripHorizontal,
    Pin,
    X,
    Plus,
} from "lucide-react";
import ScrollbarWrapper from "./ScrollbarWrapper";
import ConfirmDeleteModal from "./ConfirmDeleteModal";

const UTILITY_SIZES = { 0: 80, 1: 50 };
// Fixed layout pixels (label-to-chevron gap, chevron width) used to compute a header's minimum width.
const HEADER_LABEL_GAP = 6;
const HEADER_CHEVRON_WIDTH = 16;
// Add entries to let more header phrases abbreviate once a column is too narrow for the full label.
const HEADER_ABBREVIATIONS = { Destination: "Dest" };

const abbreviateHeaderLabel = (label) =>
    Object.entries(HEADER_ABBREVIATIONS).reduce(
        (result, [phrase, short]) => result.replace(new RegExp(phrase, "g"), short),
        label,
    );

const columnSizingEqual = (a, b) => {
    const aKeys = Object.keys(a || {});
    const bKeys = Object.keys(b || {});
    return aKeys.length === bKeys.length && aKeys.every((k) => a[k] === b[k]);
};

const pinningFromStickyIndices = (stickyIndices, columnIds) => ({
    left: (stickyIndices || []).map((i) => columnIds[i]).filter(Boolean),
    right: [],
});

const reorderIdsInArray = (ids, srcId, tgtId) => {
    const next = [...ids];
    const srcIdx = next.indexOf(srcId);
    const tgtIdx = next.indexOf(tgtId);
    if (srcIdx === -1 || tgtIdx === -1) return ids;
    next.splice(srcIdx, 1);
    const adjustedTarget = srcIdx < tgtIdx ? tgtIdx - 1 : tgtIdx;
    next.splice(adjustedTarget, 0, srcId);
    return next;
};

const filterDataCols = (cols) => cols.filter((c) => !c.columnDef.meta?.isUtility);

// ─────────────────────────────────────────────────────────────────────────────
// ColumnManagerPanel
// ─────────────────────────────────────────────────────────────────────────────
const ColumnManagerPanel = ({
    table,
    columnPinning,
    columnOrder,
    columnVisibility,
    nonHideableColumnIds,
    onRestoreDefaults,
    onSaveView,
    saveViewDisabled = false,
    saveViewLabel = "Save View",
    onSaveNewView,
    saveNewViewLabel = "Save as new view",
    onDeleteView,
    deleteViewDisabled = false,
    deleteViewLabel = "Delete view",
    deleteViewName,
    onClose,
    open = true,
}) => {
    const [showSaveNewInput, setShowSaveNewInput] = useState(false);
    const [newViewName, setNewViewName] = useState("");
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const nonHideable = useMemo(() => new Set(nonHideableColumnIds), [nonHideableColumnIds]);
    const pinnedLeft = useMemo(
        () => filterDataCols(table.getLeftVisibleLeafColumns()).filter((c) => c.getIsVisible()),
        [table, columnPinning, columnOrder, columnVisibility],
    );
    const center = useMemo(
        () => filterDataCols(table.getCenterVisibleLeafColumns()).filter((c) => c.getIsVisible()),
        [table, columnPinning, columnOrder, columnVisibility],
    );
    const pinnedRight = useMemo(
        () => filterDataCols(table.getRightVisibleLeafColumns()).filter((c) => c.getIsVisible()),
        [table, columnPinning, columnOrder, columnVisibility],
    );
    const hiddenCols = useMemo(
        () => filterDataCols(table.getAllLeafColumns()).filter((c) => !c.getIsVisible()),
        [table, columnPinning, columnOrder, columnVisibility],
    );

    const dragSrc = useRef(null);
    const [dragOver, setDragOver] = useState(null);

    const hideColumn = (column) => {
        if (nonHideable.has(column.id)) return;
        column.toggleVisibility(false);
    };

    const showColumn = (column) => {
        column.toggleVisibility(true);
    };

    const reorderWithinSection = (sectionCols, sectionKey, from, to) => {
        if (from === to) return;
        const srcId = sectionCols[from].id;
        const tgtId = sectionCols[to].id;

        if (sectionKey === "left" || sectionKey === "right") {
            const side = sectionKey === "left" ? "left" : "right";
            table.setColumnPinning((pinning) => {
                const current = pinning[side] ?? [];
                const nextSide = reorderIdsInArray(current, srcId, tgtId);
                return nextSide === current ? pinning : { ...pinning, [side]: nextSide };
            });
            return;
        }

        table.setColumnOrder((order) => reorderIdsInArray(order, srcId, tgtId));
    };

    const togglePin = (column) => {
        column.pin(column.getIsPinned() ? false : "left");
    };

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    useEffect(() => {
        if (open) return;
        setShowSaveNewInput(false);
        setNewViewName("");
    }, [open]);

    const renderSection = (sectionCols, sectionKey, label) => {
        if (sectionCols.length === 0 && sectionKey !== "center") return null;

        return (
            <div key={sectionKey}>
                <div className="flex items-center gap-1.5 px-3 pt-2.5 pb-1">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                        {label}
                    </span>
                    {sectionKey !== "center" && (
                        <Pin size={9} className="text-[#223688] dark:text-[#5d6fc0] fill-current rotate-45" />
                    )}
                </div>

                {sectionCols.length === 0 ? (
                    <p className="px-3 pb-2 text-[11px] italic text-gray-300 dark:text-gray-600">
                        None
                    </p>
                ) : (
                    <ul className="px-2 pb-1">
                        {sectionCols.map((col, i) => {
                            const colLabel = col.columnDef.meta?.label ?? col.id;
                            const isPinned = col.getIsPinned() !== false;
                            const canHide = !nonHideable.has(col.id);
                            const isDragOver = dragOver?.section === sectionKey && dragOver?.i === i;

                            return (
                                <li
                                    key={col.id}
                                    draggable
                                    onDragStart={() => { dragSrc.current = { section: sectionKey, i }; }}
                                    onDragOver={(e) => {
                                        e.preventDefault();
                                        if (dragSrc.current?.section !== sectionKey) return;
                                        setDragOver({ section: sectionKey, i });
                                    }}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        if (dragSrc.current?.section === sectionKey) {
                                            reorderWithinSection(sectionCols, sectionKey, dragSrc.current.i, i);
                                        }
                                        dragSrc.current = null;
                                        setDragOver(null);
                                    }}
                                    onDragEnd={() => { dragSrc.current = null; setDragOver(null); }}
                                    className={`flex items-center gap-2 px-2 py-1.5 rounded-md cursor-grab select-none
                                        transition-colors group
                                        ${isDragOver
                                            ? "bg-[#223688]/10 dark:bg-[#223688]/25 border border-[#223688]/40 dark:border-[#5d6fc0]/50"
                                            : "hover:bg-gray-50 dark:hover:bg-acs-dark-hover border border-transparent"
                                        }`}
                                >
                                    <GripHorizontal
                                        size={13}
                                        className="flex-shrink-0 text-gray-300 dark:text-gray-600 group-hover:text-gray-400 transition-colors"
                                    />

                                    <span className="flex-1 text-xs text-gray-700 dark:text-acs-dark-text truncate">
                                        {colLabel}
                                    </span>

                                    <button
                                        type="button"
                                        title={isPinned ? "Unpin column" : "Pin column left"}
                                        onClick={() => togglePin(col)}
                                        className={`flex-shrink-0 p-0.5 rounded transition-colors
                                            ${isPinned
                                                ? "text-[#223688] hover:text-[#1a2b6d] dark:text-[#5d6fc0] dark:hover:text-[#8290d0]"
                                                : "text-gray-300 hover:text-gray-500 dark:text-gray-600 dark:hover:text-gray-400"
                                            }`}
                                    >
                                        <Pin
                                            size={12}
                                            className={isPinned ? "fill-current rotate-45" : ""}
                                        />
                                    </button>

                                    {canHide && (
                                        <button
                                            type="button"
                                            title="Remove column"
                                            onClick={() => hideColumn(col)}
                                            className="flex-shrink-0 p-0.5 rounded text-gray-300 hover:text-red-500 dark:text-gray-600 dark:hover:text-red-400 transition-colors"
                                        >
                                            <X size={13} />
                                        </button>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        );
    };

    if (!open) return null;

    const sectionsChildren = (
        <>
            {renderSection(pinnedLeft, "left", "Pinned")}

            {pinnedLeft.length > 0 && (
                <div className="mx-3 my-1 border-t border-dashed border-gray-200 dark:border-acs-dark-border" />
            )}

            {renderSection(center, "center", "Unpinned")}

            {pinnedRight.length > 0 && (
                <div className="mx-3 my-1 border-t border-dashed border-gray-200 dark:border-acs-dark-border" />
            )}

            {renderSection(pinnedRight, "right", "Pinned Right")}

            {hiddenCols.length > 0 && (
                <>
                    <div className="mx-3 my-1 border-t border-dashed border-gray-200 dark:border-acs-dark-border" />
                    <div>
                        <div className="flex items-center gap-1.5 px-3 pt-2.5 pb-1">
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                                Hidden
                            </span>
                        </div>
                        <ul className="px-2 pb-1">
                            {hiddenCols.map((col) => {
                                const colLabel = col.columnDef.meta?.label ?? col.id;
                                return (
                                    <li
                                        key={col.id}
                                        className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-gray-50 dark:hover:bg-acs-dark-hover"
                                    >
                                        <span className="flex-1 text-xs text-gray-500 dark:text-gray-400 truncate">
                                            {colLabel}
                                        </span>
                                        <button
                                            type="button"
                                            title="Show column"
                                            onClick={() => showColumn(col)}
                                            className="flex-shrink-0 flex items-center justify-center h-5 w-5 rounded-full bg-[#223688] text-white hover:bg-[#1a2b6d] transition-colors"
                                        >
                                            <Plus size={13} strokeWidth={2.5} />
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </>
            )}
        </>
    );

    const footer = (
        <div className="flex flex-shrink-0 items-center justify-between gap-2 border-t border-gray-200 px-4 py-3 dark:border-acs-dark-border">
            <div>
                {onDeleteView && (
                    <button
                        type="button"
                        onClick={() => setConfirmingDelete(true)}
                        disabled={deleteViewDisabled}
                        className="rounded-full px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed dark:text-red-400 dark:hover:bg-red-950/30"
                    >
                        {deleteViewLabel}
                    </button>
                )}
            </div>
            <div className="flex items-center gap-2">
                {showSaveNewInput ? (
                    <div className="flex flex-col items-end gap-2">
                        <input
                            type="text"
                            value={newViewName}
                            onChange={(e) => setNewViewName(e.target.value)}
                            placeholder="View name"
                            autoFocus
                            className="w-48 rounded border px-2 py-1 text-sm dark:border-acs-dark-border dark:bg-acs-dark-surface"
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && newViewName.trim()) {
                                    onSaveNewView?.(newViewName.trim());
                                    setShowSaveNewInput(false);
                                    setNewViewName("");
                                }
                                if (e.key === "Escape") {
                                    setShowSaveNewInput(false);
                                    setNewViewName("");
                                }
                            }}
                        />
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowSaveNewInput(false);
                                    setNewViewName("");
                                }}
                                className="rounded-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 dark:text-acs-dark-text dark:hover:bg-acs-dark-hover"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                disabled={!newViewName.trim()}
                                onClick={() => {
                                    onSaveNewView?.(newViewName.trim());
                                    setShowSaveNewInput(false);
                                    setNewViewName("");
                                }}
                                className="rounded-full bg-[#223688] px-4 py-1.5 text-sm font-semibold text-white hover:bg-[#1a2b6d] disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                Save
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-full px-4 py-1.5 text-sm text-gray-700 hover:bg-gray-100 dark:text-acs-dark-text dark:hover:bg-acs-dark-hover"
                        >
                            Close
                        </button>
                        {onSaveView ? (
                            <button
                                type="button"
                                onClick={onSaveView}
                                disabled={saveViewDisabled}
                                className="rounded-full bg-[#223688] px-4 py-1.5 text-sm font-semibold text-white hover:bg-[#1a2b6d] disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {saveViewLabel}
                            </button>
                        ) : null}
                        {onSaveNewView ? (
                            <button
                                type="button"
                                onClick={() => setShowSaveNewInput(true)}
                                className="rounded-full bg-[#223688] px-4 py-1.5 text-sm font-semibold text-white hover:bg-[#1a2b6d]"
                            >
                                {saveNewViewLabel}
                            </button>
                        ) : !onSaveView ? (
                            <button
                                type="button"
                                onClick={onRestoreDefaults}
                                className="rounded-full bg-[#223688] px-4 py-1.5 text-sm font-semibold text-white hover:bg-[#1a2b6d]"
                            >
                                Restore to defaults
                            </button>
                        ) : null}
                    </>
                )}
            </div>
        </div>
    );

    const usageHint = (
        <p className="flex-shrink-0 px-4 pt-3 pb-1 text-[11px] text-gray-400 dark:text-gray-500">
            Use <Pin size={9} className="inline relative -top-px" /> to pin · drag to reorder · <X size={9} className="inline relative -top-px" /> to remove.
        </p>
    );

    const deleteConfirm = (
        <ConfirmDeleteModal
            isOpen={confirmingDelete}
            title="Delete view"
            headline={`Delete the \u201c${deleteViewName ?? ""}\u201d view?`}
            description="This cannot be undone. The table keeps its current layout — only the saved view is deleted."
            confirmLabel="Delete view"
            overlayClassName="z-[10001]"
            onCancel={() => setConfirmingDelete(false)}
            onConfirm={() => {
                setConfirmingDelete(false);
                onDeleteView?.();
            }}
        />
    );


    // Conventional centered modal with a dimmed backdrop.
    const modal = (
        <div
            className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-4"
            onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        >
            <div
                data-column-manager-panel
                role="dialog"
                aria-modal="true"
                className="flex w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl dark:bg-acs-dark-surface"
                style={{ maxHeight: "80vh" }}
            >
                <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-acs-dark-border">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-acs-dark-text">
                        Manage columns
                    </h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-acs-dark-hover"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto pb-2">
                    {usageHint}
                    {sectionsChildren}
                </div>

                {footer}
            </div>
        </div>
    );

    return ReactDOM.createPortal(
        <>
            {modal}
            {deleteConfirm}
        </>,
        document.body,
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Header renderers (columnDef.header via flexRender)
// ─────────────────────────────────────────────────────────────────────────────
const UtilityCheckboxHeader = ({ table }) => {
    const {
        onSelectAll,
        renderCheckboxActions,
        checkboxHeaderRef,
        selectAllCheckboxRef,
    } = table.options.meta;

    return (
        <div className="flex items-center justify-center gap-fluid-1 pl-fluid-2.5 ml-fluid-1 relative" ref={checkboxHeaderRef}>
            <input
                ref={selectAllCheckboxRef}
                type="checkbox"
                className="relative appearance-none cursor-pointer block w-[15px] h-[15px] rounded-sm border border-gray-400 dark:border-acs-dark-border bg-white dark:bg-acs-dark-surface transition-none checked:bg-acs-checkbox dark:checked:bg-acs-checkbox-dark checked:border-acs-checkbox after:content-[''] after:absolute after:inset-0 after:flex after:items-center after:justify-center checked:after:content-['✓'] checked:after:text-white checked:after:text-[12px] checked:after:font-bold"
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => onSelectAll?.(e.target.checked)}
            />
            {renderCheckboxActions && (
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); renderCheckboxActions(e); }}
                    className="text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-acs-dark-text transition-none"
                >
                    <ChevronDown size={14} />
                </button>
            )}
        </div>
    );
};

const UtilityActionHeader = () => (
    <div className="flex items-center justify-center">
        <MoreHorizontal size={16} className="text-gray-600 dark:text-gray-300" />
    </div>
);

const DataColumnHeader = ({ column, header, table }) => {
    const {
        enableColumnPin,
        enableColumnResize,
        onSort,
        sortBy,
        ascending,
        sortKeyByHeader,
        saveScrollBeforeSort,
        reportHeaderMinWidth,
    } = table.options.meta;

    const headerRef = useRef(null);
    const fullMeasureRef = useRef(null);
    const shortMeasureRef = useRef(null);

    const headerLabel = String(column.columnDef.meta?.label ?? column.id);
    // See HEADER_ABBREVIATIONS above for which phrases can shorten once too narrow for the full label.
    const shortHeaderLabel = abbreviateHeaderLabel(headerLabel);
    const hasAbbreviation = shortHeaderLabel !== headerLabel;
    const pinned = column.getIsPinned();
    const canSort = column.columnDef.enableSorting === true;
    const canPin = column.getCanPin() && enableColumnPin;

    const currentSortKey = typeof sortKeyByHeader === "function"
        ? sortKeyByHeader(headerLabel)
        : null;
    const isActive = currentSortKey ? sortBy === currentSortKey : sortBy === headerLabel;

    const rightSlots = (canPin ? 1 : 0) + (enableColumnResize ? 1 : 0);
    const rightPad = rightSlots * 20 + 4;

    // Full-label width needed; determines whether the header shows the full or abbreviated text.
    const [fullLabelMinWidth, setFullLabelMinWidth] = useState(0);

    // Measures hidden label clones (the visible label may show either variant) to keep minSize matched to actual header content.
    useLayoutEffect(() => {
        if (typeof reportHeaderMinWidth !== "function") return;
        const fullEl = fullMeasureRef.current;
        const headerEl = headerRef.current;
        if (!fullEl || !headerEl) return;

        const measure = () => {
            const thEl = headerEl.closest("th");
            const thStyle = thEl ? getComputedStyle(thEl) : null;
            const thPadding = thStyle
                ? parseFloat(thStyle.paddingLeft || "0") + parseFloat(thStyle.paddingRight || "0")
                : 0;
            const extras = HEADER_LABEL_GAP + HEADER_CHEVRON_WIDTH + rightPad + thPadding;

            const fullWidth = Math.ceil(fullEl.scrollWidth + extras);
            setFullLabelMinWidth(fullWidth);

            const shortEl = shortMeasureRef.current;
            const floorWidth = hasAbbreviation && shortEl
                ? Math.ceil(shortEl.scrollWidth + extras)
                : fullWidth;
            reportHeaderMinWidth(column.id, floorWidth);
        };

        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, [headerLabel, shortHeaderLabel, hasAbbreviation, rightPad, column.id, reportHeaderMinWidth]);

    const displayLabel = column.getSize() >= fullLabelMinWidth ? headerLabel : shortHeaderLabel;

    const triggerSort = (e) => {
        e.stopPropagation();
        saveScrollBeforeSort();
        onSort?.(headerLabel);
    };

    return (
        <div
            ref={headerRef}
            className={`flex items-center gap-1.5 text-gray-700 dark:text-acs-dark-text relative select-none ${
                canSort ? "cursor-pointer" : ""
            }`}
            style={{ paddingRight: `${rightPad}px` }}
            onClick={canSort ? triggerSort : undefined}
            onKeyDown={
                canSort
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            triggerSort(e);
                        }
                    }
                    : undefined
            }
            role={canSort ? "button" : undefined}
            tabIndex={canSort ? 0 : undefined}
            title={canSort ? `Sort by ${headerLabel}` : undefined}
        >
            {/* `truncate` clips to the line box, and `leading-none` made that box
                exactly 1em — shorter than the font's own glyph box, so descenders
                (y, g, p) lost their tails. */}
            <span className="truncate leading-tight">{displayLabel}</span>
            <span ref={fullMeasureRef} aria-hidden="true" className="absolute invisible whitespace-nowrap pointer-events-none">
                {headerLabel}
            </span>
            {hasAbbreviation && (
                <span ref={shortMeasureRef} aria-hidden="true" className="absolute invisible whitespace-nowrap pointer-events-none">
                    {shortHeaderLabel}
                </span>
            )}

            {canSort ? (
                <button
                    type="button"
                    onClick={triggerSort}
                    className={`flex-shrink-0 transition-none ${isActive ? "opacity-100" : "opacity-50 hover:opacity-80"}`}
                    aria-label={`Sort by ${headerLabel}`}
                >
                    <ChevronDown size={14} className={`transition-transform duration-150 ${isActive && ascending ? "rotate-180" : ""}`} />
                </button>
            ) : (
                <ChevronDown size={14} className="flex-shrink-0 text-gray-500 dark:text-gray-400 opacity-60" />
            )}

            {canPin && (
                <button
                    type="button"
                    title={pinned ? "Unpin column" : "Pin column left"}
                    data-no-sort

                    onClick={(e) => { e.stopPropagation(); column.pin(pinned ? false : "left"); }}
                    className={`flex-shrink-0 transition-colors absolute top-1/2 -translate-y-1/2 ${
                        enableColumnResize ? "right-5" : "right-0"
                    } ${pinned
                        ? "text-[#223688] hover:text-[#1a2b6d] dark:text-[#5d6fc0] dark:hover:text-[#8290d0]"
                        : "text-gray-300 hover:text-gray-500 dark:text-gray-600 dark:hover:text-gray-400"
                    }`}
                >
                    <Pin size={12} className={`transition-transform ${pinned ? "fill-current rotate-45" : ""}`} />
                </button>
            )}

            {enableColumnResize && (
                <span
                    data-no-sort
                    className="inline-flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-col-resize select-none absolute right-0 top-1/2 -translate-y-1/2"
                    onMouseDown={(e) => {
                        e.stopPropagation();
                        header.getResizeHandler()?.(e);
                    }}
                    onTouchStart={(e) => {
                        e.stopPropagation();
                        header.getResizeHandler()?.(e);
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <GripVertical size={14} />
                </span>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Table
// ─────────────────────────────────────────────────────────────────────────────
const Table = ({
    headers,
    data,
    children,
    onSelectAll,
    renderCheckboxActions,
    checkboxHeaderRef,
    headerCheckboxRef,
    selectedRows = [],
    rowClassName,
    renderChildRows,
    totalValidRows,
    autoGrowColumns = false,

    dataColumnMinWidth = 200,
    dataColumnMaxWidth = 420,
    // Floor for user resizing; distinct from dataColumnMinWidth, which only sets the default width for unsized columns.
    dataColumnResizeMinWidth = 60,

    columnSizeConfig = {},
    initialColumnWidths,
    onColumnResize,
    enableColumnResize = false,

    initialColumnPinning,
    onColumnPinningChange,
    enableColumnPin = false,
    /** @deprecated Use initialColumnPinning */
    stickyColumns,
    /** When this changes (e.g. saved view id), column order/pinning reset to match headers. */
    columnLayoutKey,

    enableColumnReorder = false,
    columnManagerOpen: controlledColumnManagerOpen,
    onColumnManagerOpenChange,
    initialColumnOrder,
    onColumnOrderChange,

    initialColumnVisibility,
    onColumnVisibilityChange,
    nonHideableColumnIds = [],
    onColumnManagerSaveView,
    columnManagerSaveViewDisabled = false,
    columnManagerSaveViewLabel = "Save View",
    onColumnManagerSaveNewView,
    columnManagerSaveNewViewLabel = "Save as new view",
    onColumnManagerDeleteView,
    columnManagerDeleteViewDisabled = false,
    columnManagerDeleteViewLabel = "Delete view",
    columnManagerDeleteViewName,


    onSort,
    sortableHeaders = [],
    sortBy,
    ascending,
    sortKeyByHeader,
    hideHeaderSortIcons = false,

}) => {
    const scrollRef = useRef(null);
    const savedScrollLeftRef = useRef(0);
    const shouldRestoreScrollRef = useRef(false);

    useLayoutEffect(() => {
        if (!shouldRestoreScrollRef.current) return;
        const el = scrollRef.current;
        if (!el) return;
        shouldRestoreScrollRef.current = false;
        requestAnimationFrame(() => { el.scrollLeft = savedScrollLeftRef.current; });
    }, [sortBy, ascending, data.length]);

    const saveScrollBeforeSort = useCallback(() => {
        savedScrollLeftRef.current = scrollRef.current?.scrollLeft ?? 0;
        shouldRestoreScrollRef.current = true;
    }, []);

    const selectAllCheckboxRef = useRef(null);
    useEffect(() => {
        const cb = selectAllCheckboxRef.current;
        if (!cb) return;
        const total = totalValidRows ?? data.length;
        const selected = selectedRows.length;
        cb.indeterminate = selected > 0 && selected < total;
        cb.checked = selected > 0 && selected >= total;
    }, [selectedRows, data.length, totalValidRows]);

    const columnIds = useMemo(
        () => headers.map((h, i) => (typeof h === "string" ? h : `col-${i}`)),
        [headers],
    );

    const resolvedInitialPinning = useMemo(() => {
        if (initialColumnPinning) return initialColumnPinning;
        if (stickyColumns) return { left: stickyColumns.map((i) => columnIds[i]).filter(Boolean), right: [] };
        return { left: columnIds.slice(0, Math.min(3, columnIds.length)), right: [] };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [columnPinning, setColumnPinning] = useState(resolvedInitialPinning);
    const [columnSizing, setColumnSizing] = useState(() => initialColumnWidths ?? {});
    // Per-column minimum width measured from its rendered header (see DataColumnHeader); floors dataColumnResizeMinWidth/columnSizeConfig.
    const [headerMinWidths, setHeaderMinWidths] = useState({});
    const reportHeaderMinWidth = useCallback((id, width) => {
        setHeaderMinWidths((prev) => (prev[id] === width ? prev : { ...prev, [id]: width }));
    }, []);

    const [columnOrder, setColumnOrder] = useState(() => initialColumnOrder ?? columnIds);
    const [columnVisibility, setColumnVisibility] = useState(() => initialColumnVisibility ?? {});
    const [internalColumnManagerOpen, setInternalColumnManagerOpen] = useState(false);
    const isColumnManagerControlled = controlledColumnManagerOpen !== undefined;
    const columnManagerOpen = isColumnManagerControlled
        ? controlledColumnManagerOpen
        : internalColumnManagerOpen;
    const setColumnManagerOpen = useCallback((updater) => {
        const apply = (prev) => (typeof updater === "function" ? updater(prev) : updater);
        if (isColumnManagerControlled) {
            onColumnManagerOpenChange?.(apply(controlledColumnManagerOpen));
        } else {
            setInternalColumnManagerOpen(apply);
        }
    }, [controlledColumnManagerOpen, isColumnManagerControlled, onColumnManagerOpenChange]);
    const prevLayoutKeyRef = useRef(columnLayoutKey);

    useEffect(() => {
        setColumnOrder((prev) => {
            const prevSet = new Set(prev);
            const next = columnIds.filter((id) => prevSet.has(id));
            columnIds.forEach((id) => { if (!prevSet.has(id)) next.push(id); });
            return next.length === prev.length && next.every((v, i) => v === prev[i]) ? prev : next;
        });
    }, [columnIds, headers.length]);

    useEffect(() => {
        if (columnLayoutKey === undefined) return;
        if (prevLayoutKeyRef.current === columnLayoutKey) return;
        prevLayoutKeyRef.current = columnLayoutKey;

        setColumnOrder(initialColumnOrder ?? columnIds);
        setColumnVisibility(initialColumnVisibility ?? {});
        setColumnPinning(
            initialColumnPinning ?? pinningFromStickyIndices(stickyColumns, columnIds)
        );
    }, [
        columnLayoutKey,
        columnIds,
        stickyColumns,
        initialColumnVisibility,
        initialColumnOrder,
        initialColumnPinning,
    ]);

    useEffect(() => {
        if (!initialColumnWidths) return;
        setColumnSizing((prev) =>
            columnSizingEqual(prev, initialColumnWidths) ? prev : initialColumnWidths
        );
    }, [initialColumnWidths]);

    // Parent notification happens in an effect (not inside the setState updater below) to avoid React's "update while rendering" warning; programmatic resets skip these refs so they don't falsely mark state dirty.
    const pendingPinningNotifyRef = useRef(null);
    const pendingSizingNotifyRef = useRef(null);
    const pendingOrderNotifyRef = useRef(null);
    const pendingVisibilityNotifyRef = useRef(null);

    const handleColumnPinningChange = useCallback((updater) => {
        setColumnPinning((prev) => {
            const next = functionalUpdate(updater, prev);
            pendingPinningNotifyRef.current = next;
            return next;
        });
    }, []);

    const handleColumnSizingChange = useCallback((updater) => {
        setColumnSizing((prev) => {
            const next = functionalUpdate(updater, prev);
            if (columnSizingEqual(prev, next)) return prev;
            pendingSizingNotifyRef.current = next;
            return next;
        });
    }, []);

    const handleColumnOrderChange = useCallback((updater) => {
        setColumnOrder((prev) => {
            const next = functionalUpdate(updater, prev);
            pendingOrderNotifyRef.current = next;
            return next;
        });
    }, []);

    const handleColumnVisibilityChange = useCallback((updater) => {
        setColumnVisibility((prev) => {
            const next = functionalUpdate(updater, prev);
            pendingVisibilityNotifyRef.current = next;
            return next;
        });
    }, []);

    useEffect(() => {
        if (pendingPinningNotifyRef.current === null) return;
        const next = pendingPinningNotifyRef.current;
        pendingPinningNotifyRef.current = null;
        onColumnPinningChange?.(next);
    }, [columnPinning, onColumnPinningChange]);

    useEffect(() => {
        if (pendingSizingNotifyRef.current === null) return;
        const next = pendingSizingNotifyRef.current;
        pendingSizingNotifyRef.current = null;
        onColumnResize?.(next);
    }, [columnSizing, onColumnResize]);

    useEffect(() => {
        if (pendingOrderNotifyRef.current === null) return;
        const next = pendingOrderNotifyRef.current;
        pendingOrderNotifyRef.current = null;
        onColumnOrderChange?.(next);
    }, [columnOrder, onColumnOrderChange]);

    useEffect(() => {
        if (pendingVisibilityNotifyRef.current === null) return;
        const next = pendingVisibilityNotifyRef.current;
        pendingVisibilityNotifyRef.current = null;
        onColumnVisibilityChange?.(next);
    }, [columnVisibility, onColumnVisibilityChange]);

    const defaultColumnOrder = initialColumnOrder ?? columnIds;

    const restoreColumnDefaults = useCallback(() => {
        handleColumnPinningChange(resolvedInitialPinning);
        handleColumnOrderChange(defaultColumnOrder);
        handleColumnVisibilityChange(initialColumnVisibility ?? {});
    }, [
        handleColumnPinningChange,
        handleColumnOrderChange,
        handleColumnVisibilityChange,
        resolvedInitialPinning,
        defaultColumnOrder,
        initialColumnVisibility,
    ]);

    const tableMeta = useMemo(
        () => ({
            onSelectAll,
            renderCheckboxActions,
            checkboxHeaderRef,
            selectAllCheckboxRef,
            enableColumnPin,
            enableColumnResize,
            onSort,
            sortBy,
            ascending,
            sortKeyByHeader,
            saveScrollBeforeSort,
            reportHeaderMinWidth,

        }),
        [
            onSelectAll,
            renderCheckboxActions,
            checkboxHeaderRef,
            enableColumnPin,
            enableColumnResize,
            onSort,
            sortBy,
            ascending,
            sortKeyByHeader,
            saveScrollBeforeSort,
            reportHeaderMinWidth,

        ],
    );

    const columns = useMemo(
        () => headers.map((header, index) => {
            const id = columnIds[index];
            const isUtility = index < 2;
            const fixedSize = UTILITY_SIZES[index] ?? 80;
            const perCol = columnSizeConfig[id] ?? {};

            const headerComponent = index === 0
                ? UtilityCheckboxHeader
                : index === 1
                    ? UtilityActionHeader
                    : DataColumnHeader;

            return {
                id,
                accessorKey: id,
                header: headerComponent,
                size: initialColumnWidths?.[id] ?? perCol.size ?? (isUtility ? fixedSize : dataColumnMinWidth),
                minSize: isUtility
                    ? fixedSize
                    : Math.max(perCol.minSize ?? dataColumnResizeMinWidth, headerMinWidths[id] ?? 0),
                maxSize: perCol.maxSize ?? (isUtility ? fixedSize : dataColumnMaxWidth),
                enableResizing: enableColumnResize && !isUtility,
                enableSorting: typeof header === "string"
                    && sortableHeaders.includes(header)
                    && typeof onSort === "function",
                enablePinning: !isUtility,
                enableHiding: !isUtility && !nonHideableColumnIds.includes(id),
                meta: { index, isUtility, label: header },
            };
        }),
        [
            headers,
            columnIds,
            dataColumnMinWidth,
            dataColumnMaxWidth,
            dataColumnResizeMinWidth,
            headerMinWidths,

            enableColumnResize,
            sortableHeaders,
            columnSizeConfig,
            initialColumnWidths,
            nonHideableColumnIds,
            onSort,
        ],
    );

    const table = useReactTable({
        data,
        columns,
        state: { columnSizing, columnPinning, columnOrder, columnVisibility },
        meta: tableMeta,
        enableColumnPinning: true,
        columnResizeMode: "onChange",
        onColumnSizingChange: handleColumnSizingChange,
        onColumnPinningChange: handleColumnPinningChange,
        onColumnOrderChange: handleColumnOrderChange,
        onColumnVisibilityChange: handleColumnVisibilityChange,
        getCoreRowModel: getCoreRowModel(),
        defaultColumn: { minSize: dataColumnResizeMinWidth, maxSize: dataColumnMaxWidth },
    });

    const columnWidthsForChildren = useMemo(
        () => Object.fromEntries(table.getFlatHeaders().map((h) => [h.id, h.getSize()])),
        [table, columnPinning, columnOrder, columnSizing, columnVisibility],
    );

    // Must match getHeaderGroups() visual order (left-pinned → center → right-pinned).
    // getVisibleLeafColumns() follows columnOrder only and diverges after pin toggles.
    const visibleLeafColumns = useMemo(() => {
        const headerCols = table.getHeaderGroups()[0]?.headers.map((h) => h.column);
        return headerCols?.length ? headerCols : table.getVisibleLeafColumns();
    }, [table, columnPinning, columnOrder, columnVisibility]);

    const handleColumnManagerSaveView = useCallback(() => {
        onColumnManagerSaveView?.({ columnOrder, columnVisibility, columnPinning });
    }, [columnOrder, columnPinning, columnVisibility, onColumnManagerSaveView]);

    const handleColumnManagerSaveNewView = useCallback(
        (viewName) => {
            onColumnManagerSaveNewView?.(viewName, {
                columnOrder,
                columnVisibility,
                columnPinning,
            });
        },
        [columnOrder, columnPinning, columnVisibility, onColumnManagerSaveNewView]
    );

    const getStickyCellProps = useCallback((colIdOrIndex) => {
        const id = typeof colIdOrIndex === "number" ? columnIds[colIdOrIndex] : colIdOrIndex;
        const column = table.getColumn(id);
        if (!column) return { className: "", style: {} };

        const pinned = column.getIsPinned();
        if (!pinned) return { className: "", style: {} };

        const isLeft = pinned === "left";
        return {
            className: "bg-white dark:bg-acs-dark-surface",
            style: {
                position: "sticky",
                zIndex: 9,
                [isLeft ? "left" : "right"]: `${isLeft ? column.getStart("left") : column.getAfter("right")}px`,
                boxShadow: isLeft
                    ? "2px 0 2px -1px rgba(0,0,0,0.1)"
                    : "-2px 0 2px -1px rgba(0,0,0,0.1)",
            },
        };
    }, [table, columnIds]);

    const getRowKey = useCallback((row, rowIndex) => (
        row.original._shipmentNumber ?? row.id ?? rowIndex
    ), []);

    return (
        <div className="relative w-full flex-1 min-h-0 h-full flex flex-col font-roboto transition-none overflow-hidden">

            {enableColumnReorder && columnManagerOpen && (
                <ColumnManagerPanel
                    table={table}
                    columnPinning={columnPinning}
                    columnOrder={columnOrder}
                    columnVisibility={columnVisibility}
                    nonHideableColumnIds={nonHideableColumnIds}
                    onRestoreDefaults={restoreColumnDefaults}
                    onSaveView={
                        onColumnManagerSaveView
                            ? handleColumnManagerSaveView
                            : undefined
                    }
                    saveViewDisabled={columnManagerSaveViewDisabled}
                    saveViewLabel={columnManagerSaveViewLabel}
                    onSaveNewView={
                        onColumnManagerSaveNewView ? handleColumnManagerSaveNewView : undefined
                    }
                    saveNewViewLabel={columnManagerSaveNewViewLabel}
                    onDeleteView={onColumnManagerDeleteView}
                    deleteViewDisabled={columnManagerDeleteViewDisabled}
                    deleteViewLabel={columnManagerDeleteViewLabel}
                    deleteViewName={columnManagerDeleteViewName}

                    onClose={() => setColumnManagerOpen(false)}
                    open={columnManagerOpen}
                />
            )}

            <ScrollbarWrapper
                ref={scrollRef}
                onScroll={(e) => {
                    if (shouldRestoreScrollRef.current) return;
                    savedScrollLeftRef.current = e.currentTarget.scrollLeft;
                }}
                className="flex-1 min-h-0 overflow-x-auto overflow-y-auto bg-white dark:bg-acs-dark-surface transition-none"
            >
                <table
                    className="min-w-full table-fixed bg-white dark:bg-acs-dark-surface border-separate border-spacing-0 align-top transition-none"
                    style={{ width: table.getTotalSize() }}
                >
                    <thead className="transition-none">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    const index = header.column.columnDef.meta?.index ?? 0;
                                    const column = header.column;
                                    const pinned = column.getIsPinned();

                                    const stickyOffset = pinned === "left"
                                        ? column.getStart("left")
                                        : pinned === "right"
                                            ? column.getAfter("right")
                                            : undefined;

                                    return (
                                        <th
                                            key={header.id}
                                            className={`
                                                ${index === 0 ? "px-0 text-center" : "px-2 sm:px-3 text-left"}
                                                py-2
                                                text-black dark:text-acs-dark-text
                                                text-[11px] sm:text-xs md:text-sm
                                                font-semibold
                                                border-b border-gray-300 dark:border-acs-dark-border
                                                whitespace-nowrap
                                                bg-gray-100 dark:bg-[#1a1c23]
                                                transition-none
                                            `}
                                            style={{
                                                position: "sticky",
                                                top: 0,
                                                left: pinned === "left" ? `${stickyOffset}px` : undefined,
                                                right: pinned === "right" ? `${stickyOffset}px` : undefined,
                                                zIndex: pinned ? 32 : 30,
                                                boxShadow: pinned === "left"
                                                    ? "2px 0 2px -1px rgba(0,0,0,0.1)"
                                                    : pinned === "right"
                                                        ? "-2px 0 2px -1px rgba(0,0,0,0.1)"
                                                        : "0 1px 0 0 rgba(0,0,0,0.08)",
                                                width: `${header.getSize()}px`,
                                                minWidth: `${header.column.columnDef.minSize}px`,
                                            }}
                                        >
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(header.column.columnDef.header, header.getContext())}
                                        </th>
                                    );
                                })}
                            </tr>
                        ))}
                    </thead>

                    <tbody>
                        {table.getRowModel().rows.map((row, rowIndex) => {
                            const originalRow = row.original;
                            return (
                                <React.Fragment key={getRowKey(row, rowIndex)}>
                                    <tr
                                        className={`
                                            hover:bg-gray-100 dark:hover:bg-acs-dark-hover
                                            cursor-pointer
                                            text-gray-800 dark:text-acs-dark-text
                                            [&>td]:py-1.5
                                            [&>td]:text-[11px]
                                            sm:[&>td]:text-xs
                                            md:[&>td]:text-sm
                                            transition-none
                                            ${rowClassName ? rowClassName(originalRow, rowIndex) : ""}
                                        `}
                                    >
                                        {children(
                                            originalRow,
                                            rowIndex,
                                            columnWidthsForChildren,
                                            getStickyCellProps,
                                            visibleLeafColumns,
                                        )}
                                    </tr>
                                    {typeof renderChildRows === "function" &&
                                        renderChildRows(originalRow, rowIndex)}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </ScrollbarWrapper>
        </div>
    );
};

export default Table;
